import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Form,
  InputNumber,
  DatePicker,
  Select,
  Button,
  Row,
  Col,
  Card,
  Tag,
  Table,
  Space,
  Badge,
  Toast,
  Typography,
} from "@douyinfe/semi-ui";
import {
  IconCoinMoneyStroked,
  IconIdCard,
  IconMoneyExchangeStroked,
  IconClear,
} from "@douyinfe/semi-icons";
import { ApiClient, Lead, MortgageLoanType } from "../../../api/ApiClient";
import { formatAmountZhCn, formatDate } from "../../../utils";
import { FormApi } from "@douyinfe/semi-ui/lib/es/form";

const apiClient = ApiClient.getDefault();
const { Text } = Typography;

interface AddLoanModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  lead?: Lead;
  teamMembersTree: any[];
}

const LOAN_TYPE_CARDS = [
  {
    key: "MORTGAGE",
    label: "信贷客户",
    icon: <IconCoinMoneyStroked size="extra-large" />,
    color: "#1677ff",
  },
  {
    key: "CARD",
    label: "信用卡客户",
    icon: <IconIdCard size="extra-large" />,
    color: "#722ed1",
  },
  {
    key: "BORROW",
    label: "个人借款",
    icon: <IconMoneyExchangeStroked size="extra-large" />,
    color: "#fa8c16",
  },
];

const LOAN_TYPE_TAG_MAP: Record<string, { text: string; color: "blue" | "purple" | "orange" | "violet" }> = {
  MORTGAGE: { text: "信贷客户", color: "blue" },
  CARD: { text: "信用卡", color: "purple" },
  BORROW: { text: "个人借款", color: "orange" },
};

function findMaxId(items: any[]): number {
  if (items.length === 0) return 0;
  return items.reduce((maxId, item) => Math.max(maxId, item.id), items[0].id);
}

function safeSumBy<T>(items: T[], prop: keyof T): number {
  return items.reduce((sum, item) => {
    const value = item[prop];
    if (typeof value === "number" && !isNaN(value)) return sum + value;
    const numericValue = Number(value);
    return !isNaN(numericValue) ? sum + numericValue : sum;
  }, 0);
}

function getNextMonthFirst(): Date {
  const now = new Date();
  const month = now.getMonth() + 1;
  return new Date(now.getFullYear(), month, 1);
}

function estimateMonthlyPayment(
  principal: number,
  termMonths: number
): number {
  if (!principal || !termMonths || principal <= 0 || termMonths <= 0) return 0;
  const annualRate = 0.049;
  const monthlyRate = annualRate / 12;
  const factor = Math.pow(1 + monthlyRate, termMonths);
  return (principal * monthlyRate * factor) / (factor - 1);
}

const AddLoanModal: React.FC<AddLoanModalProps> = ({
  visible,
  onClose,
  onSuccess,
  lead,
  teamMembersTree,
}) => {
  const [formApi, setFormApi] = useState<FormApi | undefined>(undefined);
  const [selectedLoan, setSelectedLoan] = useState<any>({});
  const [selectedLoanList, setSelectedLoanList] = useState<any[]>([]);
  const [currentLoanType, setCurrentLoanType] = useState("MORTGAGE");
  const [saveLoanSaving, setSaveLoanSaving] = useState(false);
  const [resolvedLead, setResolvedLead] = useState<Lead | undefined>(
    undefined
  );

  const editMode = !!selectedLoan && !!selectedLoan.id;

  useEffect(() => {
    if (visible) {
      setSelectedLoanList([]);
      setSelectedLoan({});
      setCurrentLoanType("MORTGAGE");
      setResolvedLead(undefined);
      formApi?.reset();
    }
  }, [visible]);

  useEffect(() => {
    if (lead && visible) {
      setResolvedLead(lead);
      setTimeout(() => {
        formApi?.setValues({
          leadId: lead.id,
          leadName: lead.name,
          applyAmount: lead.applyAmount,
          loanType: "MORTGAGE",
        });
      }, 100);
    }
  }, [lead, visible]);

  const queryLead = (leadId: any) => {
    if (leadId) {
      apiClient.getLead(leadId).then((resp) => {
        if (resp) {
          setResolvedLead(resp);
          formApi?.setValue("leadName", resp.name);
          formApi?.setValue("applyAmount", resp.applyAmount);
        } else {
          setResolvedLead(undefined);
          formApi?.setValue("leadName", undefined);
          formApi?.setValue("applyAmount", undefined);
          Toast.info("客户不存在");
        }
      });
    }
  };

  const handleLoanTypeChange = (value: string) => {
    setCurrentLoanType(value);
    formApi?.setValue("loanType", value);
    if (value === "MORTGAGE") {
      formApi?.setValue("firstPayDate", getNextMonthFirst());
    } else if (value === "BORROW") {
      formApi?.setValue("borrowDate", new Date());
      formApi?.setValue("platform", "个人借款");
    }
  };

  const handleFormSubmit = async (type: string) => {
    if (formApi?.getValue("loanType") === "CARD") {
      formApi.setValue("oneTermAmount", 0);
    }
    if (formApi?.getValue("loanType") === "BORROW") {
      formApi.setValue("platform", "个人借款");
    }
    await formApi?.validate();
    const values: any = formApi?.getValues();
    if (values["amount"] <= 0) {
      Toast.info("贷款（借款）金额必须大于0");
      return;
    }
    if (
      values["loanType"] === "MORTGAGE" &&
      (!values["term"] || values["term"] <= 0)
    ) {
      Toast.info("请填写按揭期数且按揭期数必须大于0");
      return;
    }
    if (
      values["loanType"] === "MORTGAGE" &&
      (!values["oneTermAmount"] || values["oneTermAmount"] <= 0)
    ) {
      Toast.info("请填写月供金额且月供金额必须大于0");
      return;
    }
    if (values["loanType"] === "BORROW" && !values["borrowDate"]) {
      Toast.info("请填写借款日期");
      return;
    }

    values["lastQueryTime"] = formatDate(values["lastQueryTime"]);
    values["firstPayDate"] = formatDate(values["firstPayDate"]);
    values["borrowDate"] = formatDate(values["borrowDate"]);

    let borrowerName = "";
    teamMembersTree.forEach((item: any) => {
      if (item.value === values.borrowerId) {
        borrowerName = item.label;
      }
    });

    if (type === "add") {
      const maxId = selectedLoanList.length > 0 ? findMaxId(selectedLoanList) : 0;
      const addData = { ...values, id: maxId + 1, borrowerName };
      setSelectedLoanList([...selectedLoanList, addData]);
    } else if (type === "modify") {
      if (!selectedLoan || !selectedLoan.id) {
        Toast.info("请选择要编辑的贷款");
        return;
      }
      const updatedData = selectedLoanList.map((p: any) =>
        p.id === selectedLoan.id
          ? { ...values, id: selectedLoan.id, borrowerName }
          : p
      );
      setSelectedLoanList(updatedData);
    }
    Toast.success("已暂存");
  };

  const handleRowClick = (record: any) => {
    if (record.key === "summary") return;
    setCurrentLoanType(record.loanType || "MORTGAGE");
    setSelectedLoan(record);
    setTimeout(() => {
      formApi?.setValues(record);
    }, 100);
  };

  const handleDeleteStaged = (id: number) => {
    const newList = selectedLoanList.filter((item: any) => item.id !== id);
    setSelectedLoanList(newList);
    if (selectedLoan?.id === id) {
      setSelectedLoan({});
    }
  };

  const handleClearForm = () => {
    formApi?.reset();
    formApi?.setValue("loanType", currentLoanType);
    if (resolvedLead) {
      formApi?.setValue("leadId", resolvedLead.id);
      formApi?.setValue("leadName", resolvedLead.name);
      formApi?.setValue("applyAmount", resolvedLead.applyAmount);
    }
    setSelectedLoan({});
  };

  const handleBatchSubmit = async () => {
    if (selectedLoanList.length === 0) {
      Toast.info("请先添加贷款");
      return;
    }
    Modal.confirm({
      title: "是否确认新增贷款信息和生成还款计划。",
      content: "",
      onOk: async () => {
        try {
          setSaveLoanSaving(true);
          await apiClient.request(
            "/mortgageLoan/batchCreate",
            "POST",
            undefined,
            selectedLoanList
          );
          Toast.info("保存贷款信息成功");
          setSaveLoanSaving(false);
          onSuccess();
          onClose();
        } catch (e) {
          setSaveLoanSaving(false);
        }
      },
    });
  };

  const summary = useMemo(() => {
    if (selectedLoanList.length > 0) {
      return {
        oneTermAmount: safeSumBy(selectedLoanList, "oneTermAmount"),
        amount: safeSumBy(selectedLoanList, "amount"),
      };
    }
    return { oneTermAmount: 0, amount: 0 };
  }, [selectedLoanList]);

  const dataWithSummary = [
    {
      key: "summary",
      leadName: "汇总",
      leadId: 999999,
      oneTermAmount: summary.oneTermAmount,
      amount: summary.amount,
    },
    ...selectedLoanList,
  ];

  const monthlyPaymentHint = useMemo(() => {
    if (currentLoanType !== "MORTGAGE") return 0;
    const amount = formApi?.getValue("amount");
    const term = formApi?.getValue("term");
    return estimateMonthlyPayment(amount, term);
  }, [currentLoanType, formApi?.getValue("amount"), formApi?.getValue("term")]);

  const stagedColumns = [
   
    {
      title: "贷款类型",
      dataIndex: "loanType",
      render: (text: string, record: any) => {
        const item = LOAN_TYPE_TAG_MAP[text] || { text, color: "violet" as const };
        return record.key === "summary" ? (
          <Text strong>汇总</Text>
        ) :(<Tag color={item.color} size="small">{item.text}</Tag>);
      },
    },
    { title: "平台", dataIndex: "platform",width:200 },
    {
      title: "贷款金额",
      dataIndex: "amount",
      render: (text: number, record: any) =>
        record.key === "summary" ? (
          <Text strong style={{ color: "#e91e63" }}>{formatAmountZhCn(text)}</Text>
        ) : (
          `${formatAmountZhCn(text)}`
        ),
    },
    { title: "按揭期数", dataIndex: "term" },
    {
      title: "月供金额",
      dataIndex: "oneTermAmount",
      render: (text: number, record: any) =>
        record.key === "summary" ? (
          <Text strong>{formatAmountZhCn(text)}</Text>
        ) : (
          text ? `${formatAmountZhCn(text)}` : ""
        ),
    },
    { title: "首次还款日", dataIndex: "firstPayDate" },
    { title: "账单日", dataIndex: "billDay" },
    { title: "出款人", dataIndex: "borrowerName" },
    { title: "出借日期", dataIndex: "borrowDate" },
    { title: "还款方式", dataIndex: "paidMethod", render:(value:any)=>{
      if("DH" == value) {
          return <Tag color="blue">代还</Tag>;
      } else if("KHZH" == value) {
          return <Tag color="grey">客户自还</Tag> 
     }
     return "";
    }
  },
    {
      title: "操作",
      dataIndex: "operation",
      fixed: "right" as const,
      render: (_: any, entity: any) =>
        entity.key === "summary" ? null : (
          <Button
            theme="borderless"
            size="small"
            type="danger"
            onClick={(e: any) => {
              e.stopPropagation();
              handleDeleteStaged(entity.id);
            }}
          >
            删除
          </Button>
        ),
    },
  ];

  return (
    <Modal
      fullScreen
      title={
        <Space>
          <span>新增贷款</span>
          {editMode && <Tag color="orange" size="small">编辑模式</Tag>}
          {!editMode && selectedLoanList.length > 0 && (
            <Tag color="blue" size="small">新增模式</Tag>
          )}
        </Space>
      }
      visible={visible}
      onCancel={onClose}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button type="tertiary" onClick={onClose}>取消</Button>
          <Button
            type="primary"
            onClick={handleBatchSubmit}
            loading={saveLoanSaving}
            disabled={selectedLoanList.length === 0}
          >
            批量提交 ({selectedLoanList.length})
          </Button>
        </div>
      }
    >
      {resolvedLead && (
        <Card
          style={{ marginBottom: 12, background: "var(--semi-color-fill-0)" }}
          bodyStyle={{ padding: "8px 16px" }}
        >
          <Space>
            <Tag color="blue" size="small">客户</Tag>
            <Text strong>ID: {resolvedLead.id}</Text>
            <Text>{resolvedLead.name}</Text>
            <Text type="secondary">签约金额: {formatAmountZhCn(resolvedLead.applyAmount)}</Text>
          </Space>
        </Card>
      )}

      <Form
        getFormApi={(api) => setFormApi(api)}
        labelPosition="left"
        labelWidth={100}
        labelAlign="right"
      >
        <Row gutter={16}>
          <Col span={8}>
            <Card title="客户信息" style={{ marginBottom: 6 }}>
              <Row gutter={8}>
                  <Col span={12} >
              <Form.InputNumber
                rules={[{ required: true, message: "请填写" }]}
                field="leadId"
                label="客户ID"
                initValue={lead?.id}
                onBlur={(e: any) => queryLead(e.target.value)}
                style={{ width: "100%" }}
              />
              </Col>
              <Col span={12} >
              <Form.Input
                field="leadName"
                label="客户姓名"
                initValue={lead?.name}
                readonly
              />
              </Col>
              </Row>
              <Row gutter={8}>
              <Col span={12} >
              <Form.InputNumber
                field="applyAmount"
                label="签约金额"
                initValue={lead?.applyAmount}
                readonly
                suffix="元"
              />
              </Col>
              </Row>
            </Card>


            <Card title="贷款详情" style={{ marginBottom: 6 }}>
              <Form.Input
                field="loanType"
                noLabel
                initValue="MORTGAGE"
                style={{ display: "none" }}
              />
              <Row gutter={8}>
                {LOAN_TYPE_CARDS.map((item) => (
                  <Col span={8} key={item.key}>
                    <div
                      onClick={() => handleLoanTypeChange(item.key)}
                      style={{
                        border:
                          currentLoanType === item.key
                            ? `2px solid ${item.color}`
                            : "1px solid var(--semi-color-border)",
                        borderRadius: 8,
                        padding: "12px 4px",
                        textAlign: "center",
                        cursor: "pointer",
                        background:
                          currentLoanType === item.key
                            ? `${item.color}10`
                            : "transparent",
                        transition: "all 0.2s",
                      }}
                    >
                      <div style={{ color: item.color }}>{item.icon}</div>
                      <div
                        style={{
                          marginTop: 4,
                          fontSize: 12,
                          fontWeight: currentLoanType === item.key ? 600 : 400,
                        }}
                      >
                        {item.label}
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>

              {currentLoanType === "MORTGAGE" && (
                <>
                  <Row gutter={12}>
                    <Col span={24}>
                      <Form.Input
                        rules={[{ required: true, message: "请填写" }]}
                        field="platform"
                        label="贷款平台"
                      />
                    </Col>
                    </Row>
                    <Row gutter={12}>
                    <Col span={12}>
                      <Form.InputNumber
                        rules={[{ required: true, message: "请填写" }]}
                        field="amount"
                        label="贷款金额"
                        suffix="元"
                      />
                    </Col>
                    <Col span={12}>
                      <Form.DatePicker
                        rules={[{ required: true, message: "请选择日期" }]}
                        field="firstPayDate"
                        label="首次还款日"
                        type="date"
                        initValue={getNextMonthFirst()}
                      />
                    </Col>
                  
                    
                    </Row>
                  <Row gutter={12}>
                    <Col span={12}>
                      <Form.InputNumber
                        rules={[{ required: true, message: "请填写" }]}
                        field="term"
                        label="按揭期数"
                        suffix="期"
                      />
                    </Col>
                    <Col span={12}>
                      <Form.InputNumber
                        rules={[{ required: true, message: "请填写" }]}
                        field="oneTermAmount"
                        label="月供金额"
                        suffix="元"
                      />
                    </Col>
                 
                  {monthlyPaymentHint > 0 && (
                    <Text
                      type="quaternary"
                      size="small"
                      style={{ marginLeft: 100, display: "block", marginBottom: 8 }}
                    >
                      参考月供: {formatAmountZhCn(monthlyPaymentHint)} (等额本息估算，仅供参考)
                    </Text>
                  )}
                  
                    
                     </Row>
                     <Row gutter={12}>
                    <Col span={24}>
                      <Form.RadioGroup
                        field="paidMethod"
                        direction="horizontal"
                        initValue="DH"
                        rules={[{ required: true, message: "请选择还款方式" }]}
                        label="还款方式"
                      >
                        <Form.Radio value="DH">代还</Form.Radio>
                        <Form.Radio value="KHZH">客户自还</Form.Radio>
                      </Form.RadioGroup>
                    </Col>
                  </Row>
                </>
              )}

              {currentLoanType === "CARD" && (
                <>
                  <Row gutter={12}>
                    <Col span={24}>
                      <Form.Input
                        rules={[{ required: true, message: "请填写" }]}
                        field="platform"
                        label="贷款平台"
                      />
                    </Col>
                    </Row>
                    <Row gutter={12}>
                    <Col span={12}>
                      <Form.InputNumber
                        rules={[{ required: true, message: "请填写" }]}
                        field="amount"
                        label="贷款金额"
                        suffix="元"
                      />
                    </Col>
                 
                    <Col span={12}>
                      <Form.DatePicker
                        rules={[{ required: true, message: "请选择日期" }]}
                        field="firstPayDate"
                        label="还款日"
                        type="date"
                      />
                    </Col>
                     </Row>
                  <Row gutter={12}>
                    <Col span={12}>
                      <Form.InputNumber
                        field="billDay"
                        label="账单日"
                      />
                    </Col>
                  </Row>
                  <Row gutter={12}>
                    <Col span={24}>
                      <Form.RadioGroup
                        field="paidMethod"
                        initValue="DH"
                        rules={[{ required: true, message: "请选择还款方式" }]}
                        label="还款方式"
                      >
                        <Form.Radio value="DH">代还</Form.Radio>
                        <Form.Radio value="KHZH">客户自还</Form.Radio>
                      </Form.RadioGroup>
                    </Col>
                  </Row>
                </>
              )}

              {currentLoanType === "BORROW" && (
                <>
                  <Row gutter={12}>
                    <Col span={12}>
                      <Form.Select
                        field="borrowerId"
                        label="出款人"
                        placeholder="请选择出款人"
                        rules={[{ required: true, message: "请选择出款人" }]}
                        optionList={teamMembersTree}
                        showClear
                        filter
                        style={{ width: "100%" }}
                      />
                    </Col>
                    <Col span={12}>
                      <Form.InputNumber
                        rules={[{ required: true, message: "请填写" }]}
                        field="amount"
                        label="出借金额"
                        suffix="元"
                      />
                    </Col>
                  </Row>
                  <Row gutter={12}>
                    <Col span={12}>
                      <Form.DatePicker
                        rules={[{ required: true, message: "请选择日期" }]}
                        field="borrowDate"
                        label="借款日期"
                        type="date"
                        initValue={new Date()}
                      />
                    </Col>
                    <Col span={12}>
                      <Form.DatePicker
                        rules={[{ required: true, message: "请选择日期" }]}
                        field="firstPayDate"
                        label="约定还款日"
                        type="date"
                      />
                    </Col>
                  </Row>
                </>
              )}
            </Card>

            <Space style={{ width: "100%", justifyContent: "center", marginTop: 4 }}>
              {editMode && (
                <Button
                  type="warning"
                  theme="solid"
                  onClick={() => handleFormSubmit("modify")}
                >
                  修改
                </Button>
              )}
              <Button
                type="primary"
                theme="solid"
                onClick={() => handleFormSubmit("add")}
              >
                新增
              </Button>
              <Button
                type="tertiary"
                icon={<IconClear />}
                onClick={handleClearForm}
              >
                清空
              </Button>
            </Space>
          </Col>

          <Col span={16}>
            <Card
              title={
                <Space>
                  已暂存贷款
                  <Badge count={selectedLoanList.length} type="primary" />
                </Space>
              }
            >
              <Table
                rowKey="id"
                onRow={(record: any) => ({
                  style: {
                    cursor: record.key === "summary" ? "default" : "pointer",
                    background:
                      selectedLoan?.id === record.id
                        ? "var(--semi-color-fill-2)"
                        : "transparent",
                    transition: "background-color 0.2s",
                  },
                  onClick: () => handleRowClick(record),
                })}
                columns={stagedColumns}
                pagination={{ pageSize: 15 }}
                dataSource={dataWithSummary}
                bordered
              />
            </Card>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default AddLoanModal;
